<template>
    <b-card header="💬 Forum Diskusi" class="forum-card">
        <div v-if="status === 1" class="forum-disabled text-center py-4">
            <i class="fa fa-lock mb-2" style="font-size: 1.5rem; color: #b7b9cc;" />
            <div class="text-muted">Forum diskusi tersedia setelah proses penerimaan</div>
        </div>
        <template v-else>
            <div ref="forumScroll" class="forum-scroll">
                <div v-if="nextCursor !== null" class="text-center py-2">
                    <b-button
                        variant="link"
                        size="sm"
                        :disabled="isLoadingMore"
                        @click="loadMoreMessages"
                    >
                        <b-spinner v-if="isLoadingMore" small />
                        <span v-else>Load pesan sebelumnya</span>
                    </b-button>
                </div>

                <div v-if="messages.length === 0 && !isLoading" class="text-center py-5">
                    <div class="empty-icon">💬</div>
                    <div class="text-muted">Belum ada diskusi.</div>
                    <small class="text-muted">Mulai percakapan...</small>
                </div>

                <div v-if="isLoading" class="text-center py-5">
                    <b-spinner label="Loading..." />
                </div>

                <div
                    v-for="msg in messages"
                    :key="msg.id"
                    class="forum-post"
                    :class="{ 'forum-post-self': isCurrentUser(msg) }"
                >
                    <div class="forum-avatar" style="background-color: #858796;">
                        {{ msg.user_name ? msg.user_name.charAt(0).toUpperCase() : '?' }}
                    </div>
                    <div class="forum-post-body">
                        <div class="d-flex align-items-center">
                            <span class="font-weight-bold" style="font-size: 0.85rem;">{{ msg.user_name }}</span>
                            <b-badge
                                :variant="getPartyVariant(msg.party)"
                                pill
                                class="ml-2"
                                style="font-size: 0.6rem;"
                            >{{ msg.party }}</b-badge>
                            <span class="ml-auto text-muted" style="font-size: 0.7rem;">{{ $helper.parseDate(msg.created_at, true) }} {{ $helper.parseHourMinute(msg.created_at) }}</span>
                        </div>
                        <div v-if="msg.message" class="message-text mt-1">{{ msg.message }}</div>
                        <div v-if="msg.media && msg.media.length > 0" class="d-flex flex-wrap mt-2">
                            <div
                                v-for="(media, mi) in msg.media"
                                :key="mi"
                                class="mr-1 mb-1 position-relative media-clickable"
                                @click="openPreview(media)"
                            >
                                <b-img-lazy
                                    v-if="!media.type || media.type === 'image'"
                                    :src="media.url"
                                    class="media-thumbnail"
                                    alt="Media"
                                />
                                <div v-else-if="media.type === 'video'" class="position-relative">
                                    <video :src="media.url" class="media-thumbnail" preload="metadata"></video>
                                    <div class="play-icon-overlay">
                                        <i class="fa fa-play"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        <div v-if="!canChat" class="forum-disabled text-center py-2" style="font-size: 0.85rem;">
                <i class="fa fa-eye mr-1" /> mode baca saja
            </div>
            <div v-else class="mt-2">
                <b-button variant="outline-primary" block class="forum-compose-btn" @click="showCompose = true">
                    <i class="fa fa-pencil-square-o mr-2" />Tulis pesan...
                </b-button>
            </div>
        </template>

        <b-modal
            v-model="showCompose"
            title="Tulis Pesan"
            centered
            size="lg"
            hide-footer
            no-close-on-backdrop
            @hidden="onComposeHidden"
        >
            <div v-if="attachments.length > 0" class="d-flex flex-wrap mb-2">
                <div
                    v-for="(att, index) in attachments"
                    :key="attIndexKey(index)"
                    class="attachment-chip d-flex align-items-center"
                >
                    <b-img
                        v-if="att.type === 'image' && att.previewUrl"
                        :src="att.previewUrl"
                        class="mr-1"
                        style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;"
                    />
                    <span class="text-truncate" style="max-width: 80px;" :title="att.name">{{ att.name }}</span>
                    <b-button
                        variant="link"
                        size="sm"
                        class="p-0 ml-1 text-danger"
                        @click="removeAttachment(index)"
                    >✕</b-button>
                    <div v-if="att.isUploading" class="w-100 mt-1">
                        <b-progress :value="100" animated height="2px" />
                    </div>
                </div>
            </div>

            <b-form-textarea
                v-model="newMessage"
                placeholder="Tulis pesan..."
                rows="3"
                max-rows="6"
                maxlength="1000"
                class="compose-textarea mb-2"
            />

            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <b-button variant="light" size="sm" @click="$refs.composeFileInput.click()">
                        <i class="fa fa-paperclip mr-1" />Lampirkan file
                    </b-button>
                    <input
                        ref="composeFileInput"
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        style="display: none;"
                        @change="onFileSelected"
                    />
                    <span class="text-muted ml-2" style="font-size: 0.7rem;">Maks 5 file</span>
                </div>
                <div class="d-flex align-items-center">
                    <span class="text-muted mr-2" style="font-size: 0.7rem;">{{ newMessage.length }}/1000</span>
                    <b-button
                        variant="primary"
                        size="sm"
                        :disabled="isSendDisabled"
                        @click="sendMessage"
                    >
                        <b-spinner v-if="isSending" small />
                        <span v-else><i class="fa fa-paper-plane mr-1" />Kirim</span>
                    </b-button>
                </div>
            </div>
        </b-modal>

        <b-modal
            v-model="showPreview"
            title="Photo/Video Preview"
            centered
            size="md"
            content-class="forum-preview-content"
            body-class="forum-preview-body"
            hide-footer
        >
            <div class="forum-preview-wrapper">
                <b-img
                    v-if="previewMedia && (!previewMedia.type || previewMedia.type === 'image')"
                    :src="previewMedia.url"
                    fluid
                    class="forum-preview-img"
                />
                <video
                    v-else-if="previewMedia && previewMedia.type === 'video'"
                    :src="previewMedia.url"
                    controls
                    autoplay
                    class="forum-preview-video"
                ></video>
            </div>
        </b-modal>
    </b-card>
</template>

<script>
import EnumPermission from "@/shared/enum-permissions";

export default {
    props: {
        varianceId: {
            type: [Number, String],
            required: true
        },
        noFaktur: {
            type: String,
            required: true
        },
        status: {
            type: Number,
            default: null
        },
        baseUrl: {
            type: String,
            default: "gold-receipt-variances"
        },
        uploadFolder: {
            type: String,
            default: "variance-chats"
        },
        chatPermission: {
            type: Number,
            default: EnumPermission.CHAT_TEMUAN_EMAS
        }
    },
    data() {
        return {
            messages: [],
            newMessage: "",
            attachments: [],
            isSending: false,
            isLoading: false,
            isLoadingMore: false,
            nextCursor: null,
            showPreview: false,
            previewMedia: null,
            showCompose: false
        };
    },
    computed: {
        isSendDisabled() {
            const noContent = !this.newMessage.trim() && this.attachments.length === 0;
            const hasPendingUploads = this.attachments.some(a => !a.uploadDone);
            return noContent || this.isSending || hasPendingUploads;
        },
        currentUser() {
            return localStorage.getItem("fullname");
        },
        canChat() {
            return ![98, 99].includes(this.status) && this.$helper.isAllowed(
                this.chatPermission.toString()
            );
        }
    },
    mounted() {
        this.loadMessages();
    },
    methods: {
        attIndexKey(index) {
            return "att-" + index + "-" + this.attachments[index].name;
        },
        isCurrentUser(msg) {
            return msg.user_name === this.currentUser;
        },
        getPartyVariant(party) {
            const map = {
                AUDIT: "danger",
                SM: "primary",
                PEI: "success"
            };
            return map[party] || "secondary";
        },
        loadMessages() {
            this.isLoading = true;
            this.$axios
                .get(`${this.baseUrl}/${this.varianceId}/chats?perPage=20`)
                .then(res => {
                    const items = res.data.data.items || [];
                    this.messages = items.reverse();
                    this.nextCursor = res.data.data.next_cursor;
                    this.scrollToBottom();
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        loadMoreMessages() {
            if (this.nextCursor === null) return;
            this.isLoadingMore = true;
            const scrollEl = this.$refs.forumScroll;
            const prevScrollHeight = scrollEl.scrollHeight;
            this.$axios
                .get(`${this.baseUrl}/${this.varianceId}/chats?perPage=20&cursor=${this.nextCursor}`)
                .then(res => {
                    const items = res.data.data.items || [];
                    const reversed = items.reverse();
                    this.messages = [...reversed, ...this.messages];
                    this.nextCursor = res.data.data.next_cursor;
                    this.$nextTick(() => {
                        const newScrollHeight = scrollEl.scrollHeight;
                        scrollEl.scrollTop = newScrollHeight - prevScrollHeight;
                    });
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isLoadingMore = false;
                });
        },
        onFileSelected(event) {
            const files = event.target.files;
            if (!files) return;
            for (let i = 0; i < files.length; i++) {
                if (this.attachments.length >= 5) {
                    this.$helper.parseError(this, { response: { data: { error: "Maksimal 5 file lampiran" } } });
                    break;
                }
                const file = files[i];
                const isImage = file.type.startsWith("image");
                const attachment = {
                    file,
                    name: file.name,
                    previewUrl: isImage ? URL.createObjectURL(file) : null,
                    type: isImage ? "image" : "video",
                    s3Path: null,
                    isUploading: false,
                    uploadDone: false
                };
                this.attachments.push(attachment);
                const index = this.attachments.length - 1;
                this.uploadFile(index);
            }
            event.target.value = "";
        },
        uploadFile(index) {
            const att = this.attachments[index];
            if (!att) return;
            att.isUploading = true;
            this.$axios
                .post("get-presigned-url", {
                    file_name: att.name,
                    file_type: att.file.type,
                    reference_key: this.noFaktur,
                    directory_path: this.uploadFolder
                })
                .then(res => {
                    const { presigned_url, file_path } = res.data.data;
                    return fetch(presigned_url, {
                        method: "PUT",
                        body: att.file,
                        headers: { "Content-Type": att.file.type }
                    }).then(s3Res => {
                        if (!s3Res.ok) throw new Error("Upload to S3 failed");
                        att.s3Path = this.uploadFolder + "/" + this.noFaktur + "/" + file_path;
                        att.uploadDone = true;
                    });
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                    this.attachments.splice(index, 1);
                })
                .finally(() => {
                    if (this.attachments[index]) {
                        this.attachments[index].isUploading = false;
                    }
                });
        },
        removeAttachment(index) {
            this.attachments.splice(index, 1);
        },
        sendMessage() {
            if (this.isSendDisabled) return;
            const noText = !this.newMessage.trim();
            const noMedia = this.attachments.length === 0;
            if (noText && noMedia) return;
            const pendingUploads = this.attachments.some(a => !a.uploadDone);
            if (pendingUploads) return;
            this.isSending = true;
            const payload = {
                message: this.newMessage.trim(),
                media: this.attachments.map(a => ({
                    path: a.s3Path,
                    type: a.type
                }))
            };
            this.$axios
                .post(`${this.baseUrl}/${this.varianceId}/chats`, payload)
                .then(res => {
                    this.messages.push(res.data.data);
                    this.newMessage = "";
                    this.attachments = [];
                    this.showCompose = false;
                    this.scrollToBottom();
                    this.$emit("message-sent");
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isSending = false;
                });
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.forumScroll;
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            });
        },
        openPreview(media) {
            this.previewMedia = media;
            this.showPreview = true;
        },
        closePreview() {
            this.showPreview = false;
            this.previewMedia = null;
        },
        onComposeHidden() {
            this.attachments = [];
            this.newMessage = "";
        }
    }
};
</script>

<style scoped>
.forum-card {
    border: 1px solid #e3e6f0;
    border-radius: 10px;
}

.forum-card >>> .card-header {
    background: linear-gradient(135deg, #4e73df 0%, #224abe 100%);
    color: #fff;
    font-weight: 600;
    border-radius: 10px 10px 0 0;
    border-bottom: none;
    padding: 0.6rem 1rem;
}

.forum-scroll {
    height: 450px;
    overflow-y: auto;
    padding: 8px 0;
}

.forum-scroll::-webkit-scrollbar {
    width: 5px;
}

.forum-scroll::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.forum-scroll::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}

.forum-scroll::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}

.forum-post {
    display: flex;
    padding: 10px 8px;
    border-radius: 8px;
    margin-bottom: 4px;
}

.forum-post-self {
    background-color: #f0f4ff;
}

.forum-avatar {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
    margin-right: 10px;
}

.forum-post-body {
    flex: 1;
    min-width: 0;
}

.message-text {
    white-space: pre-wrap;
}

.media-thumbnail {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
}

.media-thumbnail:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border-color: #adb5bd;
}

.play-icon-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.attachment-chip {
    background: #f1f1f1;
    border-radius: 16px;
    padding: 4px 10px;
    margin-right: 6px;
    margin-bottom: 4px;
    font-size: 0.8rem;
    position: relative;
}

.forum-compose-btn {
    border-radius: 20px;
    text-align: left;
    font-size: 0.9rem;
    color: #858796;
    border-color: #ddd;
    background: #fafbfc;
}

.forum-compose-btn:hover {
    background: #f0f4ff;
    color: #4e73df;
    border-color: #4e73df;
}

.compose-textarea {
    border-radius: 10px;
    border: 1px solid #ddd;
    font-size: 0.9rem;
}

.compose-textarea:focus {
    border-color: #4e73df;
    box-shadow: 0 0 0 2px rgba(78, 115, 223, 0.15);
}

.empty-icon {
    font-size: 2rem;
    margin-bottom: 4px;
}

.media-clickable {
    cursor: pointer;
}

.forum-preview-content {
    background: transparent;
    border: none;
}

.forum-preview-body {
    padding: 0;
    position: relative;
}

.forum-preview-close {
    position: absolute;
    top: -40px;
    right: -10px;
    z-index: 10;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.forum-preview-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
}

.forum-preview-img {
    max-height: 80vh;
    object-fit: contain;
    border-radius: 8px;
}

.forum-preview-video {
    max-height: 80vh;
    max-width: 100%;
    border-radius: 8px;
    outline: none;
}
</style>
